import axios from 'axios'
import { generateHmacHeaders } from '@/helpers/hmac'
import { lsGet, LsGlobalKeys } from '@/helpers/local-storage'
import { Session, useAuthStore } from '@/store/auth'
import { parseErrors } from '@/helpers/error-parsers'

// const API_HOST = process.env.VUE_APP_API_HOST

const API_HOST = process.env.VUE_APP_API_HOST

// const baseURL = API_HOST
//   ? `${API_HOST}/admin`
//   : 'https://app.rentbutik.com/api/admin'
const baseURL = `${API_HOST}/admin`

const client = axios.create({
  baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': 'en',
  },
  timeout: 30000, // 30 second timeout for admin panel operations
})

client.interceptors.request.use(config => {
  const session = getStoredSession()
  const secret = session?.hmac_secret || 'default_fallback_key'

  if (config.method && config.url) {
    const hmacHeaders = generateHmacHeaders({
      method: config.method,
      url: config.baseURL?.replace(/https?:\/\/[^/]+/, '') + config.url,
      data: config.data,
      secret,
    })

    config.headers = {
      ...config.headers,
      'X-Hmac-Secret': secret,
      ...hmacHeaders,
    }
  }

  return config
})

/*
 * A GET that got no response due to network drop is retried, so a short
 * network blip does not end up as a full-page error. Timeouts (ECONNABORTED)
 * are not retried to prevent doubling server load and freezing the UI for 60+s.
 */
const NETWORK_ERROR_RETRIES = 2

client.interceptors.response.use(
  response => response,
  async e => {
    const config = e.config

    if (!e.response && config?.method === 'get' && e.code !== 'ECONNABORTED') {
      const retryCount = config.retryCount || 0

      if (retryCount < NETWORK_ERROR_RETRIES) {
        config.retryCount = retryCount + 1
        await new Promise(resolve =>
          setTimeout(resolve, 1000 * config.retryCount)
        )
        return client(config)
      }
    }

    const errorStatus = parseErrors(e).errorStatus

    if (errorStatus === 401) {
      const store = useAuthStore()
      store.signOut()
    }

    return Promise.reject(e)
  }
)

export const getStoredSession = (): Session | null => {
  const session = lsGet(LsGlobalKeys.AUTH)

  if (!session) {
    return null
  }

  try {
    const parsedSession = JSON.parse(session) as Session
    return parsedSession
  } catch (error) {
    console.error('🔍 getStoredSession - failed to parse session:', error)
    return null
  }
}

export const setToken = (token: string) => {
  client.defaults.headers.common.Authorization = 'Bearer ' + token
}

export const removeToken = () => {
  delete client.defaults.headers.common.Authorization
}

export default client
